'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Search, Clock, X, Sparkles, BookOpen, Trash2, Bookmark } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import BookBadge, { BookType } from '@/components/BookBadge';
import AIQuestionCounter from '@/components/AIQuestionCounter';
import { searchIslamicTexts, SearchResultItem } from '@/lib/typesense';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useAICount } from '@/contexts/AICountContext';
import { logEvent } from '@/lib/analytics';

const BOOKMARKS_KEY = 'imaan_mate_bookmarks';

interface SavedPassageHP {
  id: string;
  book: BookType;
  title: string;
  snippet: string;
  savedAt: number;
}

function loadBookmarksHP(): SavedPassageHP[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveBookmarksHP(bookmarks: SavedPassageHP[]) {
  try {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
  } catch {
    // ignore
  }
}

const SEARCH_HISTORY_KEY = 'imaan_mate_search_history';
const MAX_HISTORY = 20;
const FREE_LIMIT = 1;

interface SearchHistoryItem {
  id: string;
  label: string;
  timestamp: number;
}

function loadSearchHistory(): SearchHistoryItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveSearchHistory(history: SearchHistoryItem[]) {
  try {
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
  } catch {
    // ignore
  }
}

function addToSearchHistory(label: string) {
  const trimmed = label.trim();
  if (!trimmed) return;
  const history = loadSearchHistory();
  const filtered = history.filter((h) => h.label.toLowerCase() !== trimmed.toLowerCase());
  const newItem: SearchHistoryItem = {
    id: `sh-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: trimmed,
    timestamp: Date.now(),
  };
  const updated = [newItem, ...filtered].slice(0, MAX_HISTORY);
  saveSearchHistory(updated);
  return updated;
}

function formatTimestamp(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const TRENDING_TOPICS = [
  { key: 'trend-charity', label: 'Sadaqah' },
  { key: 'trend-forgiveness', label: 'Seeking forgiveness' },
];

const BOOK_FILTERS: { key: string; book: BookType; icon: string; filterValue: string }[] = [
  { key: 'bf-tafsir', book: 'tafsir', icon: '📖', filterValue: 'Tafsir of Quran' },
  { key: 'bf-prophets', book: 'prophets', icon: '🌙', filterValue: 'Stories of Prophets' },
  { key: 'bf-bukhari', book: 'bukhari', icon: '📜', filterValue: 'Sahih Bukhari' },
  { key: 'bf-muslim', book: 'muslim', icon: '✨', filterValue: 'Sahih Muslim' },
];

const BOOK_TYPE_MAP: Record<string, BookType> = {
  tafsir: 'tafsir',
  prophets: 'prophets',
  bukhari: 'bukhari',
  muslim: 'muslim',
  'tafsir of quran': 'tafsir',
  'stories of prophets': 'prophets',
  'sahih bukhari': 'bukhari',
  'sahih muslim': 'muslim',
};

function getBookType(book: string): BookType {
  return BOOK_TYPE_MAP[book?.toLowerCase()] || 'bukhari';
}

const PREVIEW_LENGTH = 300;
const FULL_LENGTH = 2000;

function HomepageResultCard({ r }: { r: SearchResultItem }) {
  const [expanded, setExpanded] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(() => {
    if (typeof window === 'undefined') return false;
    return loadBookmarksHP().some((b) => b.id === String(r.id));
  });
  const preview = r.text.slice(0, PREVIEW_LENGTH);
  const fullText = r.text.slice(0, FULL_LENGTH);
  const hasMore = r.text.length > PREVIEW_LENGTH;

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    const bookmarks = loadBookmarksHP();
    const id = String(r.id);
    if (isBookmarked) {
      saveBookmarksHP(bookmarks.filter((b) => b.id !== id));
      setIsBookmarked(false);
    } else {
      const newEntry: SavedPassageHP = {
        id,
        book: getBookType(r.book),
        title: r.title || '',
        snippet: r.text.slice(0, 300),
        savedAt: Date.now(),
      };
      saveBookmarksHP([newEntry, ...bookmarks]);
      setIsBookmarked(true);
      logEvent('item_saved');
    }
  };

  return (
    <div
      className="block rounded-2xl border border-border bg-card p-4 hover:border-accent/30 transition-all duration-200 cursor-pointer"
      onClick={() => hasMore && setExpanded((v) => !v)}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BookBadge book={getBookType(r.book)} size="sm" />
        </div>
        <button
          onClick={handleBookmark}
          className={`p-1.5 rounded-lg transition-all duration-150 shrink-0 ${
            isBookmarked
              ? 'text-accent bg-accent/10' :'text-muted-foreground hover:text-accent hover:bg-accent/10'
          }`}
          aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this passage'}
        >
          <Bookmark size={15} fill={isBookmarked ? 'currentColor' : 'none'} />
        </button>
      </div>
      {r.title ? (
        <p className="text-sm font-semibold text-foreground mb-1.5 leading-snug">{r.title}</p>
      ) : null}
      <p className="text-sm text-muted-foreground leading-relaxed">
        {expanded ? fullText : preview}
        {!expanded && hasMore && <span className="text-accent font-medium">…</span>}
        {expanded && r.text.length > FULL_LENGTH && <span className="text-accent font-medium">…</span>}
      </p>
      {hasMore && (
        <button
          className="mt-2 flex items-center gap-1 text-xs font-semibold text-accent hover:underline"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export default function HomepageContent() {
  const [query, setQuery] = useState('');
  const [activeBook, setActiveBook] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [nowTick, setNowTick] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSavedRef = useRef<string>('');

  const { user, loading: authLoading } = useAuth();
  const { questionsUsed: aiUsed, loaded: aiCountLoaded } = useAICount();
  const supabase = createClient();

  // Load history from localStorage on mount
  useEffect(() => {
    setSearchHistory(loadSearchHistory());
  }, []);

  // Tick every minute to refresh relative timestamps
  useEffect(() => {
    const interval = setInterval(() => setNowTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const runSearch = useCallback(async (q: string, book: string | null) => {
    if (!q.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const data = await searchIslamicTexts(q, book || undefined);
    setResults(data.slice(0, 10));
    setIsSearching(false);
    logEvent('search_performed');
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(query, activeBook);
      // Save to history after debounce if query is meaningful and different from last saved
      if (query.trim().length >= 2 && query.trim() !== searchSavedRef.current) {
        searchSavedRef.current = query.trim();
        const updated = addToSearchHistory(query.trim());
        if (updated) setSearchHistory(updated);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, activeBook, runSearch]);

  const handleHistorySearch = (label: string) => {
    setQuery(label);
    inputRef.current?.focus();
  };

  const handleClearOne = (id: string) => {
    const updated = searchHistory.filter((h) => h.id !== id);
    setSearchHistory(updated);
    saveSearchHistory(updated);
  };

  const handleClearAll = () => {
    setSearchHistory([]);
    saveSearchHistory([]);
  };

  const showResults = query.trim().length > 0;

  // suppress nowTick lint warning — used to force re-render for timestamps
  void nowTick;

  return (
    <div className="flex flex-col min-h-screen max-w-lg mx-auto px-4 pb-24">
      {/* Top Header */}
      <header className="flex items-center justify-between pt-12 pb-6">
        <div className="flex items-center gap-2.5">
          <AppLogo size={36} />
          <span className="font-extrabold text-lg tracking-tight text-foreground">Imaan Mate</span>
        </div>
        <AIQuestionCounter used={aiUsed} total={FREE_LIMIT} compact />
      </header>

      {/* Bismillah */}
      <div className="text-center mb-8">
        <p
          className="text-3xl mb-1 leading-loose"
          style={{ fontFamily: 'serif', color: 'var(--accent)' }}
          dir="rtl"
        >
          بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْمِ
        </p>
        <p className="text-xs text-muted-foreground">
          In the name of Allah, the Most Gracious, the Most Merciful
        </p>
      </div>

      {/* Hero Search */}
      <div className="relative mb-4">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find Islamic texts by keyword..."
            className="w-full pl-11 pr-11 py-4 rounded-2xl text-sm font-medium transition-all duration-200 outline-none"
            style={{
              background: 'var(--card)',
              border: '1.5px solid var(--border)',
              color: 'var(--foreground)',
            }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-colors"
              aria-label="Clear search"
            >
              <X size={14} className="text-muted-foreground" />
            </button>
          )}
        </div>
        {!query && (
          <p className="text-[11px] text-muted-foreground text-center mt-2">
            Searches across Quran Tafsir, Hadith &amp; Prophetic Stories
          </p>
        )}
        <p className="text-[11px] text-muted-foreground/70 text-center mt-1.5">
          Can&apos;t find what you&apos;re looking for? Get a complete answer from our AI Scholar in the{' '}
          <Link href="/ai-chat-page" className="underline underline-offset-2 hover:text-muted-foreground transition-colors">
            AI Chat
          </Link>{' '}
          section.
        </p>
      </div>

      {/* Book Filter Badges */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto scrollbar-hide pb-1">
        <button
          onClick={() => setActiveBook(null)}
          className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
            activeBook === null
              ? 'bg-accent text-accent-foreground border-accent'
              : 'border-border text-muted-foreground hover:border-accent/50 hover:text-foreground'
          }`}
          style={{ background: activeBook === null ? 'var(--accent)' : undefined }}
        >
          All Books
        </button>
        {BOOK_FILTERS.map(({ key, book, filterValue }) => (
          <button
            key={key}
            onClick={() => setActiveBook(activeBook === filterValue ? null : filterValue)}
            className={`shrink-0 transition-all duration-150 rounded-full ${
              activeBook === filterValue ? 'ring-2 ring-accent ring-offset-1 ring-offset-background' : ''
            }`}
          >
            <BookBadge book={book} size="md" />
          </button>
        ))}
      </div>

      {/* Search Results (instant) */}
      {showResults && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Search size={14} className="text-accent" />
              <span className="text-xs font-semibold text-foreground">
                {isSearching ? 'Searching…' : `${results.length} results`}
              </span>
            </div>
            <Link
              href={`/search-results-page?q=${encodeURIComponent(query)}${activeBook ? `&book=${activeBook}` : ''}`}
              className="text-xs text-accent font-semibold hover:underline"
            >
              View all →
            </Link>
          </div>

          {isSearching ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={`skel-${i}`} className="rounded-2xl border border-border bg-card p-4">
                  <div className="skeleton-shimmer h-4 w-28 rounded mb-3" />
                  <div className="skeleton-shimmer h-3.5 w-2/3 rounded mb-2" />
                  <div className="skeleton-shimmer h-3 w-full rounded mb-2" />
                  <div className="skeleton-shimmer h-3 w-3/4 rounded" />
                </div>
              ))}
            </div>
          ) : results.length > 0 ? (
            <div className="space-y-3">
              {results.map((r) => (
                <HomepageResultCard key={r.id} r={r} />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 rounded-2xl border border-border bg-card">
              <BookOpen size={32} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground mb-1">No results found</p>
              <p className="text-xs text-muted-foreground">
                Try different keywords or browse by book
              </p>
            </div>
          )}
        </div>
      )}

      {/* Search History */}
      {!showResults && searchHistory.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Search History
              </span>
            </div>
            <button
              onClick={handleClearAll}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear all search history"
            >
              <Trash2 size={11} />
              <span>Clear all</span>
            </button>
          </div>
          <div className="flex flex-col gap-1.5">
            {searchHistory.map(({ id, label, timestamp }) => (
              <div
                key={id}
                className="flex items-center gap-2 group rounded-xl px-3 py-2 hover:bg-muted/60 transition-colors"
              >
                <Clock size={12} className="text-muted-foreground shrink-0" />
                <button
                  onClick={() => handleHistorySearch(label)}
                  className="flex-1 text-left text-sm text-foreground font-medium truncate hover:text-accent transition-colors"
                >
                  {label}
                </button>
                <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                  {formatTimestamp(timestamp)}
                </span>
                <button
                  onClick={() => handleClearOne(id)}
                  className="shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-muted transition-all duration-150"
                  aria-label={`Remove "${label}" from history`}
                >
                  <X size={11} className="text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Chat CTA */}
      {!showResults && (
        <Link
          href="/ai-chat-page"
          className="flex items-center gap-3 p-4 rounded-2xl border border-accent/30 mb-6 hover:border-accent/60 transition-all duration-200 group animate-pulse-gold"
          style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(27,67,50,0.3) 100%)' }}
        >
          <div className="p-2.5 rounded-xl bg-accent/15 group-hover:bg-accent/25 transition-colors">
            <Sparkles size={20} className="text-accent" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-foreground">Ask the AI Scholar</p>
            <p className="text-xs text-muted-foreground">
              Get answers with Islamic sources cited
            </p>
          </div>
          <span className="text-xs font-semibold text-accent-foreground border border-accent/30 px-2.5 py-1 rounded-full bg-[rgba(201,168,76,1)]">
            {aiCountLoaded ? `${Math.max(0, FREE_LIMIT - aiUsed)} free` : '1 free'}
          </span>
        </Link>
      )}

      {/* Trending Topics */}
      {!showResults && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-accent" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Trending Topics
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {TRENDING_TOPICS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setQuery(label)}
                className="text-xs font-medium px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-accent/50 hover:text-foreground transition-all duration-150"
                style={{ background: 'var(--card)' }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
